import DOMPurify from "dompurify"

import type { ParsedSupportingContentItem } from "./supporting-content-utils"

import { getM365TypeLabel } from "../utils/m365-citation-display"

function getText(el?: Element | null): string {
  if (!el) return ""
  return (el.textContent || "").trim()
}

function sanitizeContent(text: string): string {
  return DOMPurify.sanitize(text)
}

function getTitleFromCitationSource(source: string): string {
  if (!source.includes(":")) return source
  return source.split(":").slice(1).join(":") || source
}

function getM365ParsedType(
  type: string,
  citationId: string
): Parameters<typeof getM365TypeLabel>[0] {
  const normalized = (type || citationId.split(":")[1] || "").toLowerCase()
  if (
    normalized === "email" ||
    normalized === "event" ||
    normalized === "file" ||
    normalized === "teams"
  ) {
    return normalized
  }
  return null
}

function formatM365Subtitle(sender: string, date: string): string | undefined {
  if (!sender && !date) return undefined
  if (!sender) return date

  let subtitle = `From: ${sender}`
  if (date) {
    const parsedDate = new Date(date)
    subtitle += Number.isNaN(parsedDate.getTime())
      ? ` • ${date}`
      : ` • ${parsedDate.toLocaleDateString()}`
  }
  return subtitle
}

function parseM365DocumentParts({
  citationId,
  contentText,
  date,
  sender,
  title,
  type,
  url,
}: {
  citationId: string
  contentText: string
  date: string
  sender: string
  title: string
  type: string
  url: string
}): ParsedSupportingContentItem | null {
  if (!citationId) return null

  const m365Type = getM365ParsedType(type, citationId)
  return {
    title: title || getTitleFromCitationSource(citationId),
    subtitle: formatM365Subtitle(sender, date),
    contentHtml: sanitizeContent(contentText),
    link: url || undefined,
    citationId,
    referenceKind: "m365",
    sourceLabel: getM365TypeLabel(m365Type),
  }
}

export function parseDocumentElement(el: Element): ParsedSupportingContentItem | null {
  const meta = el.getElementsByTagName("metadata").item(0)
  const documentId = el.getAttribute("id") || ""
  const sourceEl = meta ? meta.getElementsByTagName("source").item(0) : null
  const typeEl = meta ? meta.getElementsByTagName("type").item(0) : null
  const titleEl = meta ? meta.getElementsByTagName("title").item(0) : null
  const subjectEl = meta ? meta.getElementsByTagName("subject").item(0) : null
  const urlEl = meta ? meta.getElementsByTagName("url").item(0) : null
  const senderEl = meta ? meta.getElementsByTagName("sender").item(0) : null
  const dateEl = meta ? meta.getElementsByTagName("date").item(0) : null
  const libraryEl = meta ? meta.getElementsByTagName("library").item(0) : null
  const filenameEl = meta ? meta.getElementsByTagName("filename").item(0) : null
  const library = getText(libraryEl)
  const filename = getText(filenameEl)
  const captionsEl = el.getElementsByTagName("captions")[0]
  const contentEl = el.getElementsByTagName("content")[0]
  const contentText = getText(captionsEl) || getText(contentEl)
  const source = getText(sourceEl)

  if (source === "microsoft_365" || documentId.startsWith("m365:")) {
    return parseM365DocumentParts({
      citationId: documentId,
      contentText,
      date: getText(dateEl),
      sender: getText(senderEl),
      title: getText(subjectEl) || getText(titleEl) || filename,
      type: getText(typeEl),
      url: getText(urlEl),
    })
  }

  const fileUrlEl = meta ? meta.getElementsByTagName("file_url").item(0) : null
  const folderUrlEl = meta ? meta.getElementsByTagName("folder_url").item(0) : null
  const fileUrl = getText(fileUrlEl)
  const folderUrl = getText(folderUrlEl)

  let link: string | undefined = undefined
  if (filename && filename.includes("#page=")) {
    const page = filename.split("#page=")[1]
    if (page && fileUrl) {
      link = `${fileUrl}#page=${page}`
    }
  }
  if (!link) {
    link = fileUrl || folderUrl || undefined
  }

  const title = filename || "Document"
  const subtitle = library || undefined
  return {
    title,
    subtitle,
    contentHtml: sanitizeContent(contentText),
    link,
    citationId: library && filename ? `${library}:${filename}` : undefined,
    referenceKind: "document",
    sourceLabel: "Document",
  }
}

function getTagText(source: string, tagName: string): string {
  const regex = new RegExp(`<${tagName}[^>]*>([\\s\\S]*?)</${tagName}>`, "i")
  const match = source.match(regex)
  return match ? match[1].trim() : ""
}

export function parseDocumentString(source: string): ParsedSupportingContentItem | null {
  const documentIdMatch = source.match(/<document[^>]*\bid\s*=\s*"([^"]*)"/i)
  const documentId = documentIdMatch ? documentIdMatch[1].trim() : ""
  const meta = getTagText(source, "metadata")
  const documentSource = getTagText(meta || source, "source")
  const type = getTagText(meta || source, "type")
  const titleFromMeta = getTagText(meta || source, "title")
  const subject = getTagText(meta || source, "subject")
  const url = getTagText(meta || source, "url")
  const sender = getTagText(meta || source, "sender")
  const date = getTagText(meta || source, "date")
  const library = getTagText(meta || source, "library")
  const filename = getTagText(meta || source, "filename")
  const fileUrl = getTagText(meta || source, "file_url")
  const folderUrl = getTagText(meta || source, "folder_url")
  const captions = getTagText(source, "captions")
  const content = getTagText(source, "content")

  const contentText = (captions || content || "").trim()

  if (documentSource === "microsoft_365" || documentId.startsWith("m365:")) {
    return parseM365DocumentParts({
      citationId: documentId,
      contentText,
      date,
      sender,
      title: subject || titleFromMeta || filename,
      type,
      url,
    })
  }

  let link: string | undefined = undefined
  if (filename && filename.includes("#page=")) {
    const page = filename.split("#page=")[1]
    if (page && fileUrl) {
      link = `${fileUrl}#page=${page}`
    }
  }
  if (!link) {
    link = fileUrl || folderUrl || undefined
  }

  const title = filename || "Document"
  const subtitle = library || undefined

  return {
    title,
    subtitle,
    contentHtml: sanitizeContent(contentText),
    link,
    citationId: library && filename ? `${library}:${filename}` : undefined,
    referenceKind: "document",
    sourceLabel: "Document",
  }
}
